import React from "react";
import { View, Text, Image, StyleSheet, Font } from "@react-pdf/renderer";
import { getErpBranchDetails, getTenantBrandImageSrcWithFallback } from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { getTenantNameUpper } from "../utils/tenantName";

// Register Gujarati font for better text rendering
Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Regular.ttf",
});

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Bold.ttf",
  fontWeight: "bold",
});

/** Split branch address into 2 lines: street/area on line 1, rest on line 2. */
function formatBranchAddressLines(address) {
  const raw = String(address || "").trim();
  if (!raw) return ["-"];
  if (raw.includes("\n")) {
    return raw
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 1) return [raw];
  if (parts.length === 2) return [`${parts[0]},`, parts[1]];
  // e.g. "34/2 Swaminarayan Nagar, Part-2," / "Nikol Road, Ahmedabad, Gujarat 382350"
  return [
    `${parts.slice(0, 2).join(", ")},`,
    parts.slice(2).join(", "),
  ];
}

const toEnglishDigits = (value) =>
  String(value ?? "").replace(/[૦-૯]/g, (digit) =>
    String("૦૧૨૩૪૫૬૭૮૯".indexOf(digit)),
  );

const styles = StyleSheet.create({
  topHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 2,
    marginBottom: 4,
    paddingTop: 2,
    marginTop: 0,
    borderBottom: "1pt solid #8c1d1a",
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 10,
    paddingRight: 10,
  },

  topHeaderText: {
    fontSize: 7,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
    color: "#8c1d1a",
  },

  topHeaderLeft: {
    width: "34%",
    alignItems: "flex-start",
  },

  topHeaderCenter: {
    width: "32%",
    alignItems: "center",
  },

  topHeaderRight: {
    width: "34%",
    alignItems: "flex-end",
  },

  header: {
    position: "relative",
    paddingTop: 4,
    paddingBottom: 5,
    paddingLeft: 8,
    paddingRight: 8,
    borderBottom: "1pt solid #8c1d1a",
  },

  logo: {
    width: 22,
    height: 22,
    marginRight: 6,
  },

  // Full-width centered brand — logo sits beside company name
  centerBlock: {
    width: "100%",
    alignItems: "center",
    paddingHorizontal: 70,
  },

  brandRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },

  companyTitle: {
    fontFamily: "Times-Bold",
    fontSize: 18,
    color: "#7d0000",
    letterSpacing: 0.3,
    textAlign: "center",
  },

  addressBlock: {
    marginTop: 2,
    width: "100%",
    alignItems: "center",
  },

  companyAddress: {
    fontSize: 7,
    marginTop: 2,
    marginBottom: 1,
    textAlign: "center",
    width: "100%",
  },

  // Mobile pinned right end
  mobileCol: {
    position: "absolute",
    right: 8,
    top: 6,
  },

  mobileText: {
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "right",
    fontFamily: "NotoSansGujaratiUI",
  },

  titleSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1pt solid #8c1d1a",
    paddingBottom: 0,
    marginBottom: 0,
    backgroundColor: "#8c1d1a",
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },

  titleText: {
    fontSize: 13,
    fontWeight: "bold",
    color: "white",
    fontFamily: "NotoSansGujaratiUI",
  },
});

const PDFHeader = ({ title, titleStyle, roomy = false }) => {
  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const companyMobile = toEnglishDigits(
    branchDetails.mobile || branchDetails.phone || "-",
  );
  const companyNameUpper = getTenantNameUpper();
  const addressLines = formatBranchAddressLines(branchDetails.address);
  // Flatten to one continuous string (no forced line breaks)
  const addressSingleLine =
    String(branchDetails.address || "")
      .replace(/\s*\n\s*/g, ", ")
      .replace(/,\s*,/g, ",")
      .replace(/\s+/g, " ")
      .trim() ||
    addressLines.join(", ").replace(/\s+/g, " ").trim() ||
    "-";

  const companyTitleStyle = roomy
    ? [
        styles.companyTitle,
        {
          fontSize: 24,
          letterSpacing: 0.3,
          textAlign: "center",
          width: "100%",
          alignSelf: "center",
        },
      ]
    : styles.companyTitle;
  // Full-width centered brand (logo + phone are absolute — don’t shift center)
  const centerBlockStyle = roomy
    ? {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 8,
      }
    : styles.centerBlock;
  const addressStyle = roomy
    ? {
        fontSize: 7,
        marginBottom: 0,
        marginTop: 3,
        textAlign: "center",
        width: "100%",
        alignSelf: "center",
        fontFamily: "Helvetica",
      }
    : styles.companyAddress;
  const mobileStyle = roomy
    ? {
        fontSize: 9,
        fontWeight: "bold",
        textAlign: "right",
        fontFamily: "NotoSansGujaratiUI",
      }
    : styles.mobileText;
  const mobileColStyle = roomy
    ? {
        position: "absolute",
        right: 8,
        top: 2,
        maxWidth: 95,
        alignItems: "flex-end",
        zIndex: 2,
      }
    : styles.mobileCol;
  const logoStyle = roomy
    ? { width: 36, height: 36, marginRight: 8 }
    : styles.logo;
  const topBarText = roomy
    ? { ...styles.topHeaderText, fontSize: 9 }
    : styles.topHeaderText;
  const topHeaderStyle = roomy
    ? {
        ...styles.topHeader,
        paddingTop: 4,
        paddingBottom: 4,
        marginBottom: 2,
      }
    : styles.topHeader;
  const headerPad = roomy
    ? { ...styles.header, paddingTop: 8, paddingBottom: 8 }
    : styles.header;

  return (
    <>
      <View style={topHeaderStyle}>
        <View style={styles.topHeaderLeft}>
          <Text style={topBarText}>|| શ્રી પદમાવતી માતાય નમ: ||</Text>
        </View>
        <View style={styles.topHeaderCenter}>
          <Text style={topBarText}>|| શ્રી ગણેશાય નમ: ||</Text>
        </View>
        <View style={styles.topHeaderRight}>
          <Text style={topBarText}>|| શ્રી બુટભવાની માતાય નમ: ||</Text>
        </View>
      </View>

      <View style={headerPad}>
        <View style={mobileColStyle}>
          <Text style={mobileStyle} wrap={false}>
            MO.: {companyMobile}
          </Text>
        </View>

        <View style={centerBlockStyle}>
          <View style={styles.brandRow}>
            <Image
              src={getTenantBrandImageSrcWithFallback("sidebar_icon")}
              style={logoStyle}
            />
            <Text style={companyTitleStyle} wrap={false}>
              {companyNameUpper}
            </Text>
          </View>
          <Text style={addressStyle} wrap={false}>
            {addressSingleLine}
          </Text>
        </View>

        {!roomy ? (
          <View style={styles.mobileCol}>
            <Text style={styles.mobileText}>MO.: {companyMobile}</Text>
          </View>
        ) : null}
      </View>

      <View style={styles.titleSection}>
        <View
          style={{
            flex: 1,
            alignItems: "center",
            paddingVertical: roomy ? 4 : 0,
          }}
        >
          <Text
            style={[
              styles.titleText,
              roomy ? { fontSize: 13 } : null,
              titleStyle,
            ]}
          >
            {title}
          </Text>
        </View>
      </View>
    </>
  );
};

export default PDFHeader;
